import React, { useEffect, useState } from "react";
import { Loader2, Wifi, WifiOff, DoorOpen, MessageSquare, CheckCircle2, Clock } from "lucide-react";
import { getJoinStatus, isEnded, isStarted } from "@/lib/telehealth";

// Live, read-only signals for an appointment: patient connection and unread
// messages. Connection comes from the join-status endpoint; messages from the
// appointment the caller already has.
export function LiveSignals({ appt }) {
  const [join, setJoin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const token = appt?.patient_link_token;
    if (!token) { setLoading(false); return undefined; }
    const pull = () => getJoinStatus(token)
      .then((d) => { if (alive) { setJoin(d); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    pull();
    const iv = setInterval(() => { if (!document.hidden) pull(); }, 8000);
    return () => { alive = false; clearInterval(iv); };
  }, [appt?.patient_link_token]);

  const ended = isEnded(appt?.status);
  const joined = join?.started === true || isStarted(appt?.status);
  const waiting = !ended && (appt?.patient_waiting || appt?.status === "waiting_room" || appt?.status === "waiting");

  let conn;
  if (ended) conn = { icon: DoorOpen, label: "Visit ended", bg: "#F1F1F0", fg: "#6C6C69", dot: "#9B9B97" };
  else if (joined) conn = { icon: Wifi, label: "Patient connected", bg: "#E7F7EC", fg: "#166534", dot: "#1F8A4C" };
  else if (waiting) conn = { icon: Clock, label: "In waiting room", bg: "#FFF4E0", fg: "#9A5A00", dot: "#F08C00" };
  else conn = { icon: WifiOff, label: "Not joined yet", bg: "#F1F1F0", fg: "#6C6C69", dot: "#C4C4C0" };

  const unread = appt?.unread_count || 0;

  const Card = ({ icon: Icon, label, sub, bg, fg, dot, testid }) => (
    <div className="flex items-center gap-2.5 rounded-[11px] px-3 py-2.5" style={{ background: "#fff", border: "1px solid #E6E6E4" }} data-testid={testid}>
      <span className="inline-flex items-center justify-center rounded-[8px]" style={{ width: 30, height: 30, background: bg, color: fg }}><Icon size={15} /></span>
      <div className="min-w-0">
        <div className="text-[12.5px] font-bold flex items-center gap-1.5" style={{ color: "#141412" }}>
          {dot ? <span className="w-[7px] h-[7px] rounded-full" style={{ background: dot }} /> : null}{label}
        </div>
        {sub ? <div className="text-[11px]" style={{ color: "#8A8A86" }}>{sub}</div> : null}
      </div>
    </div>
  );

  return (
    <div className="p-3 space-y-2.5" data-testid="tm-live-signals">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[.04em]" style={{ color: "#8A8A86" }}>Live signals</span>
        {loading ? <Loader2 size={12} className="animate-spin" style={{ color: "#9B9B97" }} /> : null}
      </div>
      <Card icon={conn.icon} label={conn.label} bg={conn.bg} fg={conn.fg} dot={conn.dot} testid="signal-connection" />
      <Card icon={unread > 0 ? MessageSquare : CheckCircle2} label={unread > 0 ? `${unread} unread message${unread === 1 ? "" : "s"}` : "No unread messages"}
        bg={unread > 0 ? "#FDEBE3" : "#F1F1F0"} fg={unread > 0 ? "#C2410C" : "#6C6C69"} testid="signal-messages" />
    </div>
  );
}
